<template>
  <div
    ref="playerCard"
    :class="[
      'player-detail-header',
      'right-image',
      { titled: isTitled(), 'is-mobile': isMobile, 'is-my-profile': isMyProfile },
    ]"
  >
    <div class="image-wrapper">
      <GamPlayerProfileImage
        class="image"
        :image-blob="player?.profileImageBlobHash || undefined"
        :expand="true"
        :is-skeleton="!player"
        :is-my-profile="isMyProfile"
        :is-uploading="isUploading"
        @expand="expandImage"
        @upload="handleUpload"
      />
    </div>

    <gam-expand-image
      v-if="selectedImage"
      ref="imageExpand"
      v-bind="selectedImage"
      @close="clearSelected"
      @change="handleUpload"
    />

    <div class="necklace-pendant">
      <slot name="pendant" />
    </div>

    <div v-if="$slots.cta" class="pendant-cta">
      <slot name="cta" />
    </div>

    <div class="identity">
      <div class="identity-name">
        <button
          type="button"
          class="identity-handle"
          :class="{ skeleton: !player }"
          :style="{ '--name-size': nameSize }"
          @click.stop="emit('username-click')"
        >
          <span v-if="player?.emoji" class="identity-emoji">{{ player.emoji }}</span>
          <span class="identity-username">@{{ player?.username || 'undefined' }}</span>
          <gam-icon v-if="player?.featured" class="featured" :name="GamIconSpecialName.BADGE_MATCH" size="14px" />
        </button>
      </div>

      <div v-if="player && (roleLabels.length || isMyProfile)" class="identity-byline">
        <span v-for="role in roleLabels" :key="role" class="byline-role">{{ role }}</span>

        <div
          v-if="isMyProfile"
          ref="cityRef"
          v-on-click-outside="closeCityEdit"
          class="identity-city"
          :class="{ 'is-editing': isEditingCity }"
        >
          <SparkMatchCity
            v-if="isEditingCity"
            class="city-field"
            :on-change="handleCityChanged"
            :max-items="3"
            :key="`reset-${cityChangeId}`"
          />
          <button
            v-else
            type="button"
            class="city-line"
            :aria-label="localize('profile.city.change')"
            @click="openCityEdit"
          >
            <GamIcon :name="GamIconName.PIN" size="14px" is-current />
            <span class="city-name" :class="{ 'is-empty': !city?.name }">
              {{ city?.name || localize('profile.city.empty') }}
            </span>
          </button>
        </div>
      </div>

      <div class="identity-chips">
        <div class="chips">
          <template v-if="player">
            <gam-chip v-for="(rating, index) in getRatingList" :key="index" v-bind="getRatingChip(rating)" />
          </template>

          <template v-else>
            <gam-chip label="1000" class="skeleton" />
          </template>
        </div>
      </div>
    </div>

    <div v-if="player && $slots['top-left']" class="detail-corner">
      <slot name="top-left" />
    </div>

    <div v-if="player" class="detail-actions">
      <slot name="top-right-actions" />
      <DetailActions v-if="!isMyProfile" v-bind="getDetailActions" />
      <!-- The people list, then settings: tools rather than things about this
           profile, so they stand apart from it as the nav's side buttons do. -->
      <div v-if="isMyProfile" class="profile-tools">
        <button
          type="button"
          class="profile-tool-btn"
          :aria-label="localize('app.nav.players')"
          @click="emit('players')"
        >
          <GamIcon :name="GamIconName.HEART_EMPTY" :size="GamIconSize.NORMAL" is-current />
        </button>

        <button
          type="button"
          class="profile-tool-btn"
          :aria-label="localize('profile.my.settings.label')"
          @click="emit('settings')"
        >
          <GamIcon :name="GamIconName.SETTINGS_GEAR_EMPTY" :size="GamIconSize.NORMAL" is-current />
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { RatingType } from '@/core/data/rating/rating.type';
import { BlobSource } from '@/core/data/report/report.type';
import type { PlayerRatingsDto, UserDto } from '@/core/data/user/user.interface';
import { PlayerType, RatingDisplayMode } from '@/core/data/user/user.type';
import { gambits, localize } from '@/core/gambits';
import PlayerHelper from '@/core/helpers/player.helper';
import { isMobile } from '@/core/helpers/ui.helper';
import { useAuthStore } from '@/stores/auth.store';
import { useCityStore } from '@/stores/city.store';
import GamChip from '@/views/components/GamChip.vue';
import GamExpandImage from '@/views/components/GamExpandImage.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamChipVariant } from '@/views/composables/constants/components/gamChip.constants';
import {
  GamIconName,
  GamIconSize,
  GamIconSpecialName,
} from '@/views/composables/constants/components/gamIcon.constants';
import type { GamChipType } from '@/views/composables/models/components/GamChip';
import type { GamDropdownItem } from '@/views/composables/models/components/GamDropdown';
import type { GamExpandImageType } from '@/views/composables/models/components/GamImage';
import DetailActions from '@/views/main/players/details/parts/DetailsActions.vue';
import SparkMatchCity from '@/views/main/sparks/components/SparkMatchCity.vue';
import { vOnClickOutside } from '@vueuse/components';
import { storeToRefs } from 'pinia';
import { computed, nextTick, ref } from 'vue';
import { useToast } from 'vue-toastification';
import GamPlayerProfileImage from './GamPlayerProfileImage.vue';
export interface GamPlayerDetailHeaderProps {
  player: UserDto | null;
}

const props = defineProps<GamPlayerDetailHeaderProps>();

// const playerCard = ref<HTMLDivElement>();
const selectedImage = ref<GamExpandImageType | null>();
const imageExpand = ref<InstanceType<typeof GamExpandImage>>();

const authStore = useAuthStore();
const { user, userDetails } = storeToRefs(authStore);

// const geolocationStore = useGeolocationStore();
// const { userLocation, isLocationPermissionGranted } = storeToRefs(geolocationStore);

const emit = defineEmits(['update', 'settings', 'players', 'challenge', 'username-click']);

const isUploading = ref(false);

const cityStore = useCityStore();
const { city } = storeToRefs(cityStore);

const cityChangeId = ref<number>(0);
const cityRef = ref<HTMLDivElement>();
const isEditingCity = ref(false);

const openCityEdit = async () => {
  isEditingCity.value = true;
  await nextTick();
  cityRef.value?.querySelector('input')?.focus();
};

const closeCityEdit = () => {
  isEditingCity.value = false;
};

const handleCityChanged = async (item: GamDropdownItem | null): Promise<void> => {
  closeCityEdit();
  if (!item?.coordinates || item?.id === city.value?.googlePlaceId) {
    return;
  }

  await cityStore.determineCity(item.coordinates);
};

const handleUpload = async (base64Image: string) => {
  isUploading.value = true;
  const result = await gambits.userService.updateImages({ profileImage: base64Image });
  if (result.data) {
    await authStore.fetchUserInfo(true);
    const newHash = userDetails.value?.profileImageBlobHash;
    if (newHash && selectedImage.value) {
      selectedImage.value.images[0].imageBlobHash = newHash;
      selectedImage.value.images[0].id = newHash;
    }
    emit('update');
  } else {
    useToast().error('Failed to upload photo.');
    if (selectedImage.value) clearSelected();
  }
  isUploading.value = false;
};

const isGambitDots = computed(() => props.player?.ratingDisplayMode === RatingDisplayMode.DOTS);

const getRatingChip = (rating?: PlayerRatingsDto): GamChipType | undefined => {
  if (!rating) return;
  const isGambit = rating.type === RatingType.GAMBIT;
  const isThisDots = isGambitDots.value && isGambit;
  return {
    label: isThisDots ? undefined : rating?.rating?.toString() || '',
    dotsRating: isThisDots ? rating.rating || undefined : undefined,
    leftIcon: rating?.type ? PlayerHelper.getPlayerRatingIcon(rating.type) : undefined,
    ...(isGambit && { variant: GamChipVariant.PRIMARY, isActive: true }),
  };
};

/* Steps down with length, so a long handle still reads whole. */
const nameSize = computed((): string => {
  const length = (props.player?.username?.length ?? 0) + 1;
  if (length <= 11) return '26px';
  if (length <= 15) return '22px';
  if (length <= 19) return '19px';
  return '17px';
});

const roleLabels = computed((): string[] => {
  const player = props.player;
  if (!player) return [];
  const labels: string[] = [];
  if (player.title) labels.push(localize(PlayerHelper.getPlayerTitleLocale(player.title, true)));
  if (player.playerTypes?.includes(PlayerType.COACH)) {
    labels.push(localize(PlayerHelper.getPlayerTypeLocale(PlayerType.COACH)));
  }
  if (player.playerTypes?.includes(PlayerType.STREAMER)) {
    labels.push(localize(PlayerHelper.getPlayerTypeLocale(PlayerType.STREAMER)));
  }
  return labels;
});

const isTitled = (): boolean => {
  return !!props.player?.title;
};

const getRatingList = computed((): PlayerRatingsDto[] => {
  const ratings = props.player?.ratings
    ?.filter((r) => r.rating && r.type !== RatingType.PUZZLE)
    .sort((a, b) => Number(b.type === RatingType.GAMBIT) - Number(a.type === RatingType.GAMBIT));
  if (!ratings) return [];

  return isMobile.value ? ratings.slice(0, 3) : ratings;
});

const isMyProfile = computed(() => {
  return user.value?.id === props.player?.id;
});

const getDetailActions = computed(() => ({
  player: props.player as UserDto,
}));

// const handlePreciseLocationRequest = async () => {
//   geolocationStore.setShouldPanToChangedUserLocation(true);

//   if (!isLocationPermissionGranted.value) {
//     geolocationStore.setLocationPermissionRequested(true);

//     gambits.geolocationService.getCurrentPosition();
//     gambits.geolocationService.startTracking();

//     if (!isSafari.value) return;
//   }

//   // @ts-ignore
//   window.gMap?.map.setZoom(17);
//   // @ts-ignore
//   window.gMap?.map?.panTo(userLocation.value?.location);
// };

// const handleOpenMyLocations = async () => {
//   const location = userLocation.value?.location;
//   if (!location) return;

//   await handlePreciseLocationRequest();

//   await router.push({
//     name: RouterNameType.LOCATIONS,
//     query: {
//       lat: location.lat,
//       lng: location.lng,
//       zoom: 17,
//     },
//   });
// };

const clearSelected = () => {
  selectedImage.value = null;
};

const expandImage = (blob: string) => {
  selectedImage.value = {
    blobSource: BlobSource.USER_PROFILE,
    selectedIndex: 0,
    images: [
      {
        id: blob,
        imageBlobHash: blob,
        liked: false,
        user: null,
      },
    ],
    closeAfterDelete: true,
    noFilter: true,
    confirmDelete: true,
    canDelete: false,
    canReport: !isMyProfile.value,
    canChange: isMyProfile.value,
  };

  nextTick(() => {
    imageExpand.value?.show();
  });
};
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
@use '@/ui/css/partial';

.player-detail-header {
  // --pendant-center is set on the profile sheet: the photo is 128px at
  // spacing-l, and the 44px glass hangs 10px under it. The name starts 16px
  // under that glass. The sheet owns the variable so the sticky stack shares it.

  min-height: 170px;
  padding: var(--spacing-l) var(--spacing-l) var(--spacing-md);
  padding-top: calc(var(--pendant-center) + 22px + var(--spacing-l));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: var(--spacing-s);

  border-radius: 0;
  background: linear-gradient(
    20deg,
    oklch(0.28 0.0125 var(--hue)) 0%,
    oklch(0.22 0.006 calc(var(--hue) + 90)) 50%,
    oklch(0.28 0.0125 calc(var(--hue) + 180)) 100%
  );
  background-color: var(--color-dark-600);

  position: relative;
  z-index: 501;
}

.player-detail-header.center-image {
  padding-top: 128px;
}

.player-detail-header {
  .left-image,
  .right-image {
    padding-top: 0;
  }
}

.image-wrapper {
  position: absolute;
  top: var(--spacing-l);
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;
}

.image {
  border: 3px solid var(--color-dark-500);
  border-radius: 100%;
}

.center-image .image-wrapper {
  top: var(--spacing-l);
  transform: translate(-50%, 0);
}

.left-image .image-wrapper {
  top: 50%;
  left: 0;
  right: unset;
  transform: translate(1rem, -50%);
}

.player-detail-header .image {
  width: 128px;
  height: 128px;
}

.necklace-pendant {
  position: absolute;
  top: var(--pendant-center);
  left: 50%;
  z-index: 6;
  transform: translate(-50%, -50%);
}

/* Beside the pendant, so the name below has the full width. */
.pendant-cta {
  position: absolute;
  top: var(--pendant-center);
  right: var(--spacing-l);
  z-index: 6;
  display: flex;
  transform: translateY(-50%);
}

/* Centered under the photo and its pendant: the name, a byline, the ratings. */
.identity {
  position: relative;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
  width: 100%;
  min-width: 0;
}

.identity-name {
  display: flex;
  justify-content: center;
  width: 100%;
  min-width: 0;
}

.identity-handle {
  --name-size: 26px;
  display: flex;
  flex-shrink: 1;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  min-width: 0;
  padding: 0;
  border: 0;
  background: none;
  color: var(--color-white);
  cursor: pointer;

  @include press;
}

.identity-emoji {
  flex-shrink: 0;
  font-size: calc(var(--name-size) * 0.92);
  line-height: 1;
}

.identity-handle .featured {
  flex-shrink: 0;
}

/* The name leads the header, ahead of the photo's pendant and the chips. */
.identity-username {
  min-width: 0;
  overflow: hidden;
  font-size: var(--name-size);
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.01em;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Who they are and where they are now, quiet under the name. */
.identity-byline {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 4px 0;
  max-width: 100%;

  > * + *::before {
    content: '·';
    margin: 0 var(--spacing-md);
    color: var(--color-white-30);
  }

  > .identity-city {
    display: flex;
    align-items: center;
  }
}

.byline-role {
  color: var(--color-white-70);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  white-space: nowrap;
}

/* Scrolls sideways when the ratings outgrow the width, centered when they fit. */
.identity-chips {
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }

  .chips {
    flex-wrap: nowrap;
    width: max-content;
    margin: 0 auto;
  }
}

/* The check-in pin, so the city reads as where you are now. Tap to change it. */
.identity-city {
  max-width: 100%;

  &.is-editing {
    width: 14rem;
  }
}

.city-line {
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 0;
  border: 0;
  background: none;
  color: var(--color-pink);
  cursor: pointer;

  @include press;
}

.city-name {
  min-width: 0;
  overflow: hidden;
  color: var(--color-white-70);
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;

  &.is-empty {
    color: var(--color-white-40);
  }
}

.detail-corner {
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 6;
  display: flex;
  align-items: flex-start;
}

.detail-actions {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 6;
  display: flex;
  align-items: center;
  gap: 8px;
}

.profile-tools {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

/* The same small steklo circle as the three dots on another player. */
.profile-tool-btn {
  width: 34px;
  height: 34px;
  padding: 0;
  border: none;
  border-radius: 34px;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  background-color: var(--color-white-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 200ms linear;

  @include hover {
    background-color: var(--color-white-20);
    color: var(--color-white);
  }

  @include press;
}

.chips {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: var(--spacing-md);
}
</style>
